import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import toast from "react-hot-toast"
import {
  resumeSitSession,
  retakeSitSession,
  startSitSession,
} from "../../../api/studentPracticePaperSit"
import {
  generatePracticePaper,
  getPracticePaper,
  getPracticePaperPdf,
  type StudentPublishedTemplate,
} from "../../../api/studentPracticePapers"
import { getLearnerApiErrorMessage } from "../../../auth/learner/errors"
import type { StudentSubject } from "../Questions/types"
import { studentPracticePaperAnalyticHref } from "../../../component/student/config/studentNavItems"
import ConfirmModal from "../components/ConfirmModal"
import AdminTemplatesStep from "./components/AdminTemplatesStep"
import ConfigureStep from "./components/ConfigureStep"
import CreatePaperDrawer from "./components/CreatePaperDrawer"
import GeneratorModeToggle from "./components/GeneratorModeToggle"
import GeneratorStepper from "./components/GeneratorStepper"
import PracticePapersHero from "./components/PracticePapersHero"
import ReadyStep from "./components/ReadyStep"
import SessionList from "./components/SessionList"
import SitMode from "./components/SitMode"
import SubjectStep from "./components/SubjectStep"
import LearnerAuthBootScreen from "../../../auth/learner/LearnerAuthBootScreen"
import useLearnerAuth from "../../../auth/learner/useLearnerAuth"
import usePaperGenerator from "./hooks/usePaperGenerator"
import usePaperSessions from "./hooks/usePaperSessions"
import {
  paperDisplayTitle,
  paperPdfDownloadName,
  type GeneratorStep,
  type PaperSession,
} from "./types"
import { canonicalGradeLevel } from "./utils/gradeLevels"
import { DEFAULT_CONTEXT_MODE } from "./utils/contextModes"
import "./PracticePapersPage.style.scss"

const PracticePapersPage = () => {
  const { student } = useLearnerAuth()
  const navigate = useNavigate()
  const {
    sessions,
    pagination,
    page,
    setPage,
    loading: sessionsLoading,
    saveSession,
    deleteSession,
  } = usePaperSessions()
  const generator = usePaperGenerator()
  const [searchParams, setSearchParams] = useSearchParams()
  const handledPaperQuery = useRef<string | null>(null)

  const [step, setStep] = useState<GeneratorStep>(1)
  const [isGenerating, setIsGenerating] = useState(false)
  const [generatingTemplateId, setGeneratingTemplateId] = useState<string | null>(
    null
  )
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null)
  const [pdfLoadingId, setPdfLoadingId] = useState<string | null>(null)
  const [launchingId, setLaunchingId] = useState<string | null>(null)
  const [launchMessage, setLaunchMessage] = useState("Opening your exam…")

  const [selectedSubjectId, setSelectedSubjectId] = useState("")
  const [selectedSubjectTitle, setSelectedSubjectTitle] = useState("")
  const [paperTitle, setPaperTitle] = useState("")
  const [paperGrade, setPaperGrade] = useState(() =>
    canonicalGradeLevel(student?.grade)
  )
  const [contextMode, setContextMode] = useState(DEFAULT_CONTEXT_MODE)
  const [selectedCount, setSelectedCount] = useState(5)
  const [useAi, setUseAi] = useState(false)
  const [templateUseAi, setTemplateUseAi] = useState(false)
  const [selectedHierarchyIds, setSelectedHierarchyIds] = useState<string[]>([])
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const createRequested = searchParams.get("create") === "1"
  const paperRequested = searchParams.get("paper")
  const retakeRequested = searchParams.get("retake") === "1"

  const activeSession = sessions.find((s) => s.id === activeSessionId) || null
  const pendingDeleteSession = useMemo(
    () => sessions.find((s) => s.id === pendingDeleteId) || null,
    [sessions, pendingDeleteId]
  )
  const selectedSubject = useMemo(
    () => generator.subjects.find((s) => s.id === selectedSubjectId),
    [generator.subjects, selectedSubjectId]
  )

  useEffect(() => {
    const fromProfile = canonicalGradeLevel(student?.grade)
    if (fromProfile) {
      setPaperGrade((current) => current || fromProfile)
    }
  }, [student?.grade])

  const handleSelectSubject = (subject: StudentSubject) => {
    setSelectedSubjectId(subject.id)
    setSelectedSubjectTitle(subject.title)
    setSelectedHierarchyIds([])
    setStep(2)
  }

  const resetGenerator = () => {
    setStep(1)
    setActiveSessionId(null)
    setSelectedSubjectId("")
    setSelectedSubjectTitle("")
    setPaperTitle("")
    setPaperGrade(canonicalGradeLevel(student?.grade))
    setContextMode(DEFAULT_CONTEXT_MODE)
    setSelectedHierarchyIds([])
    setSelectedCount(5)
    setUseAi(false)
    setTemplateUseAi(false)
    setLaunchingId(null)
  }

  const createBusy = isGenerating || !!generatingTemplateId
  const isFocusMode = step === 4 || !!launchingId
  const createOpen = createRequested && !isFocusMode

  const clearCreateQuery = useCallback(() => {
    setSearchParams(
      (prev) => {
        if (prev.get("create") !== "1") return prev
        const next = new URLSearchParams(prev)
        next.delete("create")
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  const clearPaperQuery = useCallback(() => {
    setSearchParams(
      (prev) => {
        if (!prev.get("paper") && !prev.get("retake")) return prev
        const next = new URLSearchParams(prev)
        next.delete("paper")
        next.delete("retake")
        return next
      },
      { replace: true }
    )
  }, [setSearchParams])

  const openCreate = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set("create", "1")
      return next
    })
  }

  const closeCreate = useCallback(() => {
    if (isGenerating || generatingTemplateId) return
    clearCreateQuery()
    setStep(1)
    setSelectedSubjectId("")
    setSelectedSubjectTitle("")
    setPaperTitle("")
    setPaperGrade(canonicalGradeLevel(student?.grade))
    setContextMode(DEFAULT_CONTEXT_MODE)
    setSelectedHierarchyIds([])
    setSelectedCount(5)
    setUseAi(false)
    setTemplateUseAi(false)
  }, [clearCreateQuery, generatingTemplateId, isGenerating, student?.grade])

  useEffect(() => {
    if (!createRequested) return
    if (step === 4 || launchingId) return
    setStep(1)
    setSelectedSubjectId("")
    setSelectedSubjectTitle("")
    setPaperTitle("")
    setPaperGrade(canonicalGradeLevel(student?.grade))
    setContextMode(DEFAULT_CONTEXT_MODE)
    setSelectedHierarchyIds([])
    setSelectedCount(5)
    setUseAi(false)
    setTemplateUseAi(false)
    // Only when ?create=1 appears — do not reset mid-wizard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createRequested])

  const scrollToMyPapers = () => {
    const page = document.querySelector(".practice-papers-page__list")
    if (page instanceof HTMLElement) {
      page.scrollTo({ top: 0, behavior: "smooth" })
      return
    }
    document.getElementById("my-papers")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    })
  }

  const queueAiPaper = (
    paper: PaperSession & { shortfall?: number }
  ) => {
    saveSession(paper)
    clearCreateQuery()
    resetGenerator()
    if (paper.status === "failed") {
      toast.error("Could not generate AI questions for this paper.")
    } else if (paper.status === "preparing") {
      toast.success("Your practice paper is generating in My Papers.")
    } else {
      toast.success("Your practice paper is ready.")
    }
    window.requestAnimationFrame(() => {
      scrollToMyPapers()
    })
  }

  const applyGeneratedPaper = (
    paper: PaperSession & { shortfall?: number },
    meta?: { subjectId?: string; subjectTitle?: string; count?: number }
  ) => {
    if (!paper.questions?.length) {
      toast.error("No matching questions found for this selection.")
      return false
    }
    if (paper.shortfall) {
      toast.error(
        `Only ${paper.questions.length} question${
          paper.questions.length === 1 ? "" : "s"
        } available — paper created with what we have.`
      )
    }
    saveSession(paper)
    setActiveSessionId(paper.id)
    if (meta?.subjectId) setSelectedSubjectId(meta.subjectId)
    if (meta?.subjectTitle) setSelectedSubjectTitle(meta.subjectTitle)
    if (meta?.count) setSelectedCount(meta.count)
    setStep(3)
    return true
  }

  const runGenerate = async (
    subjectId: string,
    subjectTitle: string,
    hierarchyIds: string[],
    count: number,
    title: string
  ) => {
    setIsGenerating(true)
    try {
      const res = await generatePracticePaper({
        subject_id: subjectId,
        hierarchy_ids: hierarchyIds,
        count,
        use_ai: useAi,
        title,
        grade: paperGrade,
        context_mode: contextMode,
      })
      if (!res.data?.success || !res.data.data) {
        throw new Error(res.data?.message || "Could not generate paper")
      }
      const paper = res.data.data as PaperSession & { shortfall?: number }
      if (useAi) {
        queueAiPaper(paper)
      } else {
        applyGeneratedPaper(paper, {
          subjectId,
          subjectTitle,
          count,
        })
        setSelectedHierarchyIds(hierarchyIds)
      }
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not generate paper"))
    } finally {
      setIsGenerating(false)
    }
  }

  const handleGenerate = () => {
    const title = paperTitle.trim()
    if (!selectedSubjectId || !title || !paperGrade) return
    void runGenerate(
      selectedSubjectId,
      selectedSubjectTitle,
      selectedHierarchyIds,
      selectedCount,
      title
    )
  }

  const handleUseTemplate = async (template: StudentPublishedTemplate) => {
    if (!template.id || generatingTemplateId) return
    setGeneratingTemplateId(template.id)
    try {
      const res = await generatePracticePaper({
        template_id: template.id,
        use_ai: templateUseAi,
        title: template.title,
        context_mode: contextMode,
      })
      if (!res.data?.success || !res.data.data) {
        throw new Error(res.data?.message || "Could not generate paper")
      }
      const paper = res.data.data as PaperSession & { shortfall?: number }
      if (templateUseAi) {
        queueAiPaper(paper)
      } else {
        applyGeneratedPaper(paper, {
          subjectId: template.subjectId,
          subjectTitle: template.subjectTitle,
          count: template.count,
        })
      }
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not generate paper"))
    } finally {
      setGeneratingTemplateId(null)
    }
  }

  const handleDownloadPdf = async (session: PaperSession) => {
    if (session.status === "preparing" || session.status === "failed") return
    if (pdfLoadingId) return
    setPdfLoadingId(session.id)
    try {
      const res = await getPracticePaperPdf(session.id)
      const url = res.data?.data?.pdf_url
      if (!res.data?.success || !url) {
        throw new Error(res.data?.message || "PDF unavailable")
      }
      // Prefer an <a> click over window.open — browsers often block
      // window.open after an async await (lost user-gesture).
      const link = document.createElement("a")
      link.href = url
      link.target = "_blank"
      link.rel = "noopener noreferrer"
      link.download = paperPdfDownloadName(session)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not download PDF"))
    } finally {
      setPdfLoadingId(null)
    }
  }

  const handleDelete = async (id: string) => {
    setIsDeleting(true)
    try {
      await deleteSession(id)
      if (activeSessionId === id) {
        setActiveSessionId(null)
        setStep(1)
      }
      setPendingDeleteId(null)
      toast.success("Practice paper deleted")
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not delete paper"))
    } finally {
      setIsDeleting(false)
    }
  }

  const startPaper = async (sessionId: string, session?: PaperSession) => {
    const s = session || sessions.find((x) => x.id === sessionId)
    if (!s || launchingId) return
    if (s.status === "preparing" || s.status === "failed") return

    const isResume = s.status === "in_progress"
    setLaunchMessage(isResume ? "Resuming your exam…" : "Opening your exam…")
    setLaunchingId(sessionId)
    setActiveSessionId(sessionId)
    clearCreateQuery()
    clearPaperQuery()
    setStep(4)

    try {
      const res = isResume
        ? await resumeSitSession(s.id)
        : await startSitSession(s.id)
      if (!res.data?.success || !res.data.data) {
        throw new Error(res.data?.message || "Could not open sit session")
      }
      const live = res.data.data as PaperSession
      if (!live.sitToken) {
        throw new Error("Sit session token missing from server response")
      }
      saveSession(live)
      setActiveSessionId(live.id)
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not start practice paper"))
      setStep(1)
      setActiveSessionId(null)
    } finally {
      setLaunchingId(null)
    }
  }

  const viewAnalytics = (sessionId: string) => {
    navigate(studentPracticePaperAnalyticHref(sessionId))
  }

  const retakeSession = async (session: PaperSession) => {
    if (launchingId) return

    setLaunchMessage("Preparing your retake…")
    setLaunchingId(session.id)
    setActiveSessionId(session.id)
    clearCreateQuery()
    clearPaperQuery()
    setStep(4)

    try {
      const res = await retakeSitSession(session.id)
      if (!res.data?.success || !res.data.data) {
        throw new Error(res.data?.message || "Could not retake paper")
      }
      const live = res.data.data as PaperSession
      if (!live.sitToken) {
        throw new Error("Sit session token missing from server response")
      }
      saveSession(live)
      setActiveSessionId(live.id)
    } catch (err) {
      toast.error(getLearnerApiErrorMessage(err, "Could not retake practice paper"))
      setStep(1)
      setActiveSessionId(null)
    } finally {
      setLaunchingId(null)
    }
  }

  useEffect(() => {
    if (!paperRequested) {
      handledPaperQuery.current = null
      return
    }
    if (createRequested || sessionsLoading || launchingId || step === 4) return
    const queryKey = `${paperRequested}:${retakeRequested ? "retake" : "open"}`
    if (handledPaperQuery.current === queryKey) return
    handledPaperQuery.current = queryKey
    const paperId = paperRequested
    const shouldRetake = retakeRequested

    const openGoalPaper = async () => {
      try {
        let session = sessions.find((item) => item.id === paperId)
        if (!session) {
          const res = await getPracticePaper(paperId)
          if (!res.data?.success || !res.data.data) {
            throw new Error(res.data?.message || "Practice paper not found")
          }
          session = res.data.data as PaperSession
          saveSession(session)
        }
        if (session.status === "preparing") {
          toast("That paper is still generating in My Papers.")
          return
        }
        if (session.status === "failed") {
          toast.error("That practice paper failed to generate.")
          return
        }
        if (session.status === "submitted" || session.status === "marked") {
          viewAnalytics(session.id)
          return
        }
        if (session.status === "abandoned") {
          if (shouldRetake) {
            await retakeSession(session)
            return
          }
          toast("This paper was abandoned — retake it from My Papers.")
          return
        }
        await startPaper(session.id, session)
      } catch (err) {
        toast.error(getLearnerApiErrorMessage(err, "Could not open that paper"))
      } finally {
        clearPaperQuery()
      }
    }

    void openGoalPaper()
    // Open once per ?paper= id after the list has loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paperRequested, retakeRequested, createRequested, sessionsLoading])

  const launchingSession =
    (launchingId && sessions.find((s) => s.id === launchingId)) || null

  if (step === 4 && launchingId && launchingSession) {
    return (
      <LearnerAuthBootScreen
        title={launchMessage}
        hint={
          paperDisplayTitle(launchingSession) || "Getting your paper ready…"
        }
      />
    )
  }

  if (
    step === 4 &&
    activeSession &&
    activeSession.sitToken &&
    (activeSession.status === "in_progress" ||
      activeSession.status === "not_started")
  ) {
    return (
      <SitMode
        key={`${activeSession.id}-${activeSession.startedAt || "new"}`}
        session={activeSession}
        onSync={saveSession}
        onClose={() => {
          setStep(1)
          setActiveSessionId(null)
        }}
        onSubmitted={(marked) => {
          saveSession(marked)
          navigate(studentPracticePaperAnalyticHref(marked.id))
        }}
      />
    )
  }

  const manualMode = step >= 1 && step <= 3

  return (
    <div className="practice-papers-page">
      <div className="practice-papers-page__main">
        <div
          className={`practice-papers-page__list${
            createOpen ? " is-hidden-mobile" : ""
          }`}
        >
          <div className="practice-papers-page__inner">
            <PracticePapersHero onCreate={openCreate} />

            <SessionList
              sessions={sessions}
              pagination={pagination}
              requestedPage={page}
              loading={sessionsLoading}
              launchingId={launchingId}
              pdfLoadingId={pdfLoadingId}
              onPageChange={setPage}
              onCreate={openCreate}
              onResume={startPaper}
              onViewAnalytics={viewAnalytics}
              onRetake={retakeSession}
              onDownloadPdf={(session) => {
                void handleDownloadPdf(session)
              }}
              onDelete={(id) => setPendingDeleteId(id)}
            />

            <ConfirmModal
              isOpen={!!pendingDeleteSession}
              title="Delete practice paper?"
              message={
                pendingDeleteSession
                  ? `Delete “${paperDisplayTitle(pendingDeleteSession)}” from My Papers? This can’t be undone.`
                  : "Delete this practice paper? This can’t be undone."
              }
              confirmLabel="Delete"
              cancelLabel="Cancel"
              tone="danger"
              isLoading={isDeleting}
              onCancel={() => {
                if (!isDeleting) setPendingDeleteId(null)
              }}
              onConfirm={() => {
                if (!pendingDeleteId) return
                void handleDelete(pendingDeleteId)
              }}
            />
          </div>
        </div>

        <CreatePaperDrawer
          open={createOpen}
          busy={createBusy}
          onClose={closeCreate}
        >
          {(manualMode || step === 5) && (
            <GeneratorModeToggle
              mode={step === 5 ? "templates" : "manual"}
              onManual={() => setStep(1)}
              onTemplates={() => setStep(5)}
            />
          )}

          {manualMode && <GeneratorStepper step={step} />}

          <div className="create-paper-drawer__card">
            {step === 1 && (
              <SubjectStep
                subjects={generator.subjects}
                loading={generator.subjectsLoading}
                error={generator.subjectsError}
                onSelect={handleSelectSubject}
              />
            )}

            {step === 2 && selectedSubjectId && (
              <ConfigureStep
                subjectId={selectedSubjectId}
                subjectTitle={selectedSubjectTitle}
                levelLabels={selectedSubject?.level_labels}
                selectedHierarchyIds={selectedHierarchyIds}
                selectedCount={selectedCount}
                paperTitle={paperTitle}
                paperGrade={paperGrade}
                contextMode={contextMode}
                useAi={useAi}
                isGenerating={isGenerating}
                onBack={() => setStep(1)}
                onHierarchyChange={setSelectedHierarchyIds}
                onSelectCount={setSelectedCount}
                onPaperTitleChange={setPaperTitle}
                onPaperGradeChange={setPaperGrade}
                onContextModeChange={setContextMode}
                onUseAiChange={setUseAi}
                onGenerate={handleGenerate}
              />
            )}

            {step === 3 && activeSession && (
              <ReadyStep
                session={activeSession}
                pdfLoading={pdfLoadingId === activeSession.id}
                starting={launchingId === activeSession.id}
                onStart={() => {
                  void startPaper(activeSession.id)
                }}
                onDownloadPdf={() => {
                  if (pdfLoadingId) return
                  void handleDownloadPdf(activeSession)
                }}
                onCreateAnother={resetGenerator}
              />
            )}

            {step === 5 && (
              <AdminTemplatesStep
                generatingTemplateId={generatingTemplateId}
                useAi={templateUseAi}
                contextMode={contextMode}
                onUseAiChange={setTemplateUseAi}
                onContextModeChange={setContextMode}
                onUseTemplate={(template) => {
                  void handleUseTemplate(template)
                }}
              />
            )}
          </div>
        </CreatePaperDrawer>
      </div>
    </div>
  )
}

export default PracticePapersPage
